/**
 * Umbauten sofort zeigen, bevor der Server sie bestätigt (Welle 7, 27.10.2026).
 *
 * Ein Umbau ging bis dahin so: Klick, warten bis der Server gespeichert und zu
 * GitHub hochgeladen hatte, warten auf den Bau, dann lud der ganze Editor neu.
 * Rund sechs Sekunden je Klick, und so lange war alles gesperrt. David: „es
 * fühlt sich alles sehr langsam und zäh an“.
 *
 * Jetzt rechnet das Panel den neuen Aufbau selbst aus und zeigt ihn sofort,
 * in der Abschnitts-Leiste und in der Vorschau. Die Aufträge gehen der Reihe
 * nach an den Server, der jeden prüft und speichert. Lehnt er einen ab, gilt
 * wieder sein Stand. So arbeiten Storyblok und Sanity auch: Die Änderung ist
 * sofort zu sehen, gespeichert wird im Hintergrund.
 *
 * Die Regeln hier sind dieselben wie im Server (SeitenplanAktionen::anwenden),
 * nur ohne Texte. Weichen sie einmal ab, schadet das nicht: Der Server rechnet
 * selbst, und seine Antwort ersetzt den gerechneten Stand.
 */
import type { PlanAbschnitt, Seitenplan, SeitenplanAktion } from '@/types/api'

/**
 * Kennungen, die der Server noch nicht vergeben hat, beginnen mit „~“. Echte
 * Kennungen haben die Form `[a-z][a-z0-9]{3}`, also verwechselt kein Teil des
 * Panels eine vorläufige mit einem Text-Schlüssel.
 */
export const VORLAEUFIG = '~'

export function istVorlaeufig(id: string): boolean {
  return id.startsWith(VORLAEUFIG)
}

export interface Auftrag {
  aktion: SeitenplanAktion
  /** Vorläufige Kennung dessen, was der Auftrag anlegt (Abschnitt, Kopie, Eintrag). */
  neu: string | null
}

/** Erste Variante eines Bausteins, wie der Server sie ohne Angabe wählt. */
export type ErsteVariante = (typ: string) => string | undefined

function verschiebe<T>(liste: T[], von: number, nach: number): T[] | null {
  if (von < 0 || nach < 0 || nach > liste.length - 1) return null
  const neu = [...liste]
  const [teil] = neu.splice(von, 1)
  neu.splice(nach, 0, teil as T)
  return neu
}

/**
 * Den Plan nach einem Auftrag. Passt der Auftrag nicht (Abschnitt weg,
 * Stelle außerhalb), bleibt der Plan, wie er ist: Dann lehnt auch der Server
 * ab, und sein Stand gilt.
 */
export function wende(plan: Seitenplan, auftrag: Auftrag, ersteVariante: ErsteVariante): Seitenplan {
  const a = auftrag.aktion
  const abschnitte = plan.abschnitte

  if (a.aktion === 'anlegen') {
    const position = a.position ?? abschnitte.length
    const variante = a.variante || ersteVariante(a.typ)
    if (auftrag.neu === null || variante === undefined || position < 0 || position > abschnitte.length) return plan
    const neu = [...abschnitte]
    neu.splice(position, 0, { id: auftrag.neu, typ: a.typ, variante })
    return { ...plan, abschnitte: neu }
  }
  // Sichtbar oder Entwurf betrifft die ganze Seite, nicht ihren Aufbau.
  if (!('abschnitt' in a)) return plan

  const ziel = a.abschnitt
  const index = abschnitte.findIndex((x) => x.id === ziel)
  const alt = abschnitte[index]
  if (alt === undefined) return plan
  const ersetze = (b: PlanAbschnitt): Seitenplan => ({
    ...plan,
    abschnitte: abschnitte.map((x, i) => (i === index ? b : x)),
  })

  switch (a.aktion) {
    case 'verschieben': {
      const neu = verschiebe(abschnitte, index, a.position)
      return neu === null ? plan : { ...plan, abschnitte: neu }
    }
    case 'duplizieren': {
      if (auftrag.neu === null) return plan
      const kopie: PlanAbschnitt = { ...alt, id: auftrag.neu }
      // Wie im Server: Eine Sprungmarke gilt nur einmal je Seite.
      delete kopie.anker
      if (alt.listen) {
        kopie.listen = Object.fromEntries(
          Object.entries(alt.listen).map(([name, eintraege]) => [
            name,
            eintraege.map((_, i) => `${auftrag.neu}.${i}`),
          ]),
        )
      }
      const neu = [...abschnitte]
      neu.splice(index + 1, 0, kopie)
      return { ...plan, abschnitte: neu }
    }
    case 'loeschen':
      return { ...plan, abschnitte: abschnitte.filter((x) => x.id !== ziel) }
    case 'variante':
      return ersetze({ ...alt, variante: a.variante })
    case 'hintergrund': {
      const b: PlanAbschnitt = { ...alt }
      if (a.hintergrund === '') delete b.hintergrund
      else b.hintergrund = a.hintergrund
      return ersetze(b)
    }
    case 'abstand': {
      const b: PlanAbschnitt = { ...alt }
      if (a.abstand === '') delete b.abstand
      else b.abstand = a.abstand
      return ersetze(b)
    }
    case 'schleier': {
      const b: PlanAbschnitt = { ...alt }
      if (a.schleier === '') delete b.schleier
      else b.schleier = a.schleier
      return ersetze(b)
    }
    case 'geraet': {
      const b: PlanAbschnitt = { ...alt }
      if (a.geraet === '') delete b.geraet
      else b.geraet = a.geraet
      return ersetze(b)
    }
    case 'bewegung': {
      const b: PlanAbschnitt = { ...alt }
      if (a.bewegung === '') delete b.bewegung
      else b.bewegung = a.bewegung
      return ersetze(b)
    }
    case 'anker': {
      const b: PlanAbschnitt = { ...alt }
      if (a.anker.trim() === '') delete b.anker
      else b.anker = a.anker.trim()
      return ersetze(b)
    }
    case 'ausblenden': {
      const b: PlanAbschnitt = { ...alt }
      if (a.versteckt) b.versteckt = true
      else delete b.versteckt
      return ersetze(b)
    }
    case 'eintrag_anlegen':
    case 'eintrag_verschieben':
    case 'eintrag_loeschen': {
      const eintraege = alt.listen?.[a.liste] ?? []
      let neu: string[] | null
      if (a.aktion === 'eintrag_anlegen') {
        const position = a.position ?? eintraege.length
        if (auftrag.neu === null || position < 0 || position > eintraege.length) return plan
        neu = [...eintraege]
        neu.splice(position, 0, auftrag.neu)
      } else {
        const weg = a.eintrag
        const stelle = eintraege.indexOf(weg)
        if (stelle < 0) return plan
        neu = a.aktion === 'eintrag_verschieben'
          ? verschiebe(eintraege, stelle, a.position)
          : eintraege.filter((e) => e !== weg)
      }
      if (neu === null) return plan
      return ersetze({ ...alt, listen: { ...(alt.listen ?? {}), [a.liste]: neu } })
    }
  }
  return plan
}

/** Was die Vorschau mit einem Abschnitt tun soll, bis der Bau ihn liefert. */
export interface VorschauAbschnitt {
  id: string
  /** Eine Kopie: so lange die Vorlage zeigen. */
  quelle?: string
  /** Neu und ohne Vorlage: ein Platzhalter mit dem Namen des Bausteins. */
  neu?: boolean
  /** Steht in der Vorschau, sieht nach dem Bau aber anders aus. */
  wartet?: boolean
}

/**
 * Der Aufbau, den die Vorschau zeigen soll, gemessen an dem, was sie zeigt.
 *
 * `gebaut` ist der Plan des letzten Baus, also das, was im Rahmen steht.
 * Verschieben und Löschen kann die Vorschau selbst (Reihenfolge, ausblenden).
 * Was Markup braucht, das es noch nicht gibt (Darstellung, Hintergrund,
 * Einträge, ein neuer Baustein), zeigt sie als „wird aktualisiert“ an, bis der
 * Bau da ist.
 *
 * `herkunft` nennt zu jeder Kopie ihre Vorlage, auch über mehrere Kopien
 * hinweg (Kopie einer Kopie).
 */
export function vorschauEntwurf(
  plan: Seitenplan,
  gebaut: Seitenplan,
  herkunft: Record<string, string>,
): VorschauAbschnitt[] {
  const imBau = new Map(gebaut.abschnitte.map((a) => [a.id, a]))
  const listen = (a: PlanAbschnitt): string => JSON.stringify(
    Object.entries(a.listen ?? {}).sort(([x], [y]) => x.localeCompare(y)),
  )
  return plan.abschnitte.map((a): VorschauAbschnitt => {
    const g = imBau.get(a.id)
    if (g !== undefined) {
      const anders = g.variante !== a.variante
        || (g.hintergrund ?? '') !== (a.hintergrund ?? '')
        || (g.abstand ?? '') !== (a.abstand ?? '')
        || (g.schleier ?? '') !== (a.schleier ?? '')
        || (g.geraet ?? '') !== (a.geraet ?? '')
        || (g.bewegung ?? '') !== (a.bewegung ?? '')
        || (g.anker ?? '') !== (a.anker ?? '')
        || (g.versteckt ?? false) !== (a.versteckt ?? false)
        || listen(g) !== listen(a)
      return anders ? { id: a.id, wartet: true } : { id: a.id }
    }
    let quelle = herkunft[a.id]
    for (let i = 0; quelle !== undefined && !imBau.has(quelle) && i < 50; i++) quelle = herkunft[quelle]
    return quelle !== undefined && imBau.has(quelle) ? { id: a.id, quelle, wartet: true } : { id: a.id, neu: true }
  })
}
